import type { BookingSuccessInfo } from '../analytics';

/**
 * Преобразует filterParams из объекта в массив объектов фильтров
 * Для airlines фильтрует только выбранные (value: true)
 * Для isAllAirlines и isImplicitAllAirlines отправляет только если true
 */
export const transformFilterParams = (
  filterParams?: Record<string, unknown>,
) => {
  if (!filterParams) return [];

  return Object.entries(filterParams).reduce(
    (acc, [name, value]) => {
      // Специальная обработка для airlines - отправляем только выбранные
      if (name === 'airlines' && typeof value === 'object' && value !== null) {
        const airlines = Object.entries(value as Record<string, unknown>)
          .filter(([, selected]) => selected === true)
          .reduce(
            (obj, [code]) => {
              obj[code] = true;
              return obj;
            },
            {} as Record<string, boolean>,
          );

        if (Object.keys(airlines).length > 0) {
          acc.push({ name, value: airlines });
        }
        return acc;
      }

      // Для isAllAirlines и isImplicitAllAirlines отправляем только если true
      if (
        (name === 'isAllAirlines' || name === 'isImplicitAllAirlines') &&
        !value
      ) {
        return acc;
      }

      acc.push({ name, value });
      return acc;
    },
    [] as Array<{ name: string; value: unknown }>,
  );
};

/**
 * Считает количество непустых багажных отделений
 */
export const calculateBaggageCount = (
  baggage: BookingSuccessInfo['baggage'],
): number => {
  return Object.values(baggage).filter(
    bag => bag && typeof bag === 'object' && Object.keys(bag).length > 0,
  ).length;
};

/**
 * Фильтрует продукты с ценой > 0
 */
export const filterProductsByPrice = (
  products: BookingSuccessInfo['products'],
): Record<string, any> | null => {
  if (!products) return null;

  const filtered = Object.fromEntries(
    Object.entries(products).filter(
      ([_, product]: [string, any]) => product?.price > 0,
    ),
  );

  return Object.keys(filtered).length > 0 ? filtered : null;
};

/**
 * Формирует массив дополнительных продуктов для отправки
 */
export const buildAdditionalProducts = (
  info: BookingSuccessInfo,
  hasBaggage: boolean,
  filteredProducts: Record<string, any> | null,
): Array<Record<string, any>> => {
  const hasProducts =
    filteredProducts && Object.keys(filteredProducts).length > 0;

  return [
    hasBaggage ? { baggage: info.baggage } : null,
    info.seatMap ? { seatMap: info.seatMap } : null,
    info.medicalInsurance ? { medicalInsurance: info.medicalInsurance } : null,
    info.roaming ? { roaming: info.roaming } : null,
    info.packageInfo && info.packageInfo.price > 0
      ? { packageInfo: info.packageInfo }
      : null,
    hasProducts ? { products: filteredProducts } : null,
  ].filter(Boolean) as Array<Record<string, any>>;
};
